// 跨窗口 store 快照的 LWW 并集合并（桌面壳 main + quickbar 双 webview）。
//
// why（desktop 审计 #1 / local-first-guardrails A「跨窗口共享 localStorage 必须 RMW」的
// store 侧补齐）：outbox 早已 read-modify-write，但 zustand persist 的快照写与 storage
// 事件 rehydrate 都是**整包覆盖**——两窗口在 150ms 尾随窗口内并发写时，先 flush 的一方
// 的快照不含对方未落盘的编辑，随后 storage 事件让对方用盘上值整包覆盖内存 → 未冲刷的
// 本地新增/编辑被静默清掉（sync-off 下真丢；sync-on 下 UI 闪现-消失-重现）。
//
// how：与 store.mergeList 同源的 per-row LWW，外加「单侧缺行」的三通道证据裁决：
//   1. outbox 待推 delete（op_kind='delete'）→ 本地删除意图 → 丢行；
//   2. outbox 待推 upsert → 未冲刷/未上行的新增 → 保行；
//   3. 近期远端 tombstone 登记（sync/remote-tombstones）→ 服务器删除（无 outbox 痕迹，
//      共享 cursor 不会重发）→ 丢行；
//   4. 无任何证据 → **保行**。代价不对称：误丢新增在 sync-off 下无自愈路径（永久丢
//      数据）；误保僵尸行可被下一次 pull 的 tombstone / 重启整包水合自愈。
// 时钟平局（updatedAt 相等）：唯一刻意不 bump 行时钟的字段是 task.tagIds（幻影时钟
// 护栏），其余字段的每次真实编辑都 bump——平局时只比 tagIds，有差取**盘上侧**
// （persisted-first：rehydrate 与 flush-RMW 两个方向都偏向已持久化值，收敛不 ping-pong，
// 与旧「整包采纳盘上」对 tagIds 的传播语义一致），无差保本地引用。
//
// 引用稳定性（guardrail E）：没变的行保原引用；整表没变时返回原数组引用——storage
// 事件高频回声下 useShallow/memo 不至于全列重渲染。
//
// 账号边界（guardrail A 跨租户隔离）：owner 不同**绝不并集**——调用方负责整包采纳
// （rehydrate 方向）或按现状 LWW 覆盖（写方向）。本模块只处理同 owner 的快照。
import { isNewer } from '@doneline/sync';
import type { Comment, Project, Section, Tag, Task } from './types';

export interface CrossWindowEvidence {
  /** outbox 里待推 upsert 的 entity_id 集（新增/编辑未上行的证据）。 */
  pendingUpsertIds: ReadonlySet<string>;
  /** outbox 里待推 delete 的 entity_id 集（本地删除意图的证据）。 */
  pendingDeleteIds: ReadonlySet<string>;
  /** 近期由 pull 应用的远端 tombstone id 集（服务器删除的证据）。 */
  remoteTombstoneIds: ReadonlySet<string>;
}

export interface PersistedEntitiesSlice {
  projects: Project[];
  sections: Section[];
  tasks: Task[];
  tags: Tag[];
  comments: Comment[];
}

function rowUpdatedAt(x: unknown): string | null {
  if (x && typeof x === 'object' && 'updatedAt' in x) {
    const v = (x as { updatedAt?: unknown }).updatedAt;
    return typeof v === 'string' ? v : null;
  }
  return null;
}

function sameTagIds(a: unknown, b: unknown): boolean {
  const ta = (a as { tagIds?: string[] }).tagIds ?? [];
  const tb = (b as { tagIds?: string[] }).tagIds ?? [];
  if (ta.length !== tb.length) return false;
  for (let i = 0; i < ta.length; i++) if (ta[i] !== tb[i]) return false;
  return true;
}

/** 单个实体表的跨窗口并集合并。local = 当前内存（或待写快照），foreign = 盘上快照。 */
export function mergeEntityLists<T extends { id: string }>(
  local: T[],
  foreign: T[],
  ev: CrossWindowEvidence,
): T[] {
  const foreignById = new Map(foreign.map((x) => [x.id, x]));
  const out: T[] = [];
  let changed = false;

  for (const row of local) {
    if (ev.pendingDeleteIds.has(row.id) || ev.remoteTombstoneIds.has(row.id)) {
      // 有删除证据的行不该出现在任何一侧的结果里（对方快照陈旧才会带着它）。
      changed = true;
      continue;
    }
    const other = foreignById.get(row.id);
    if (!other) {
      // 只在本地：待推 upsert = 未冲刷的新增 → 保；无证据也保（见文件头第 4 条）。
      out.push(row);
      continue;
    }
    foreignById.delete(row.id);
    const lts = rowUpdatedAt(row);
    const fts = rowUpdatedAt(other);
    if (lts && fts && isNewer(lts, fts)) {
      out.push(row); // 本地严格更新 → 保本地
    } else if (lts && fts && isNewer(fts, lts)) {
      out.push(other); // 盘上严格更新 → 采纳
      changed = true;
    } else if (!sameTagIds(row, other)) {
      out.push(other); // 时钟平局 + tagIds 有差 → persisted-first（见文件头）
      changed = true;
    } else {
      out.push(row); // 平局同值 → 保原引用（引用稳定性）
    }
  }

  // 只在盘上：删除证据 → 丢；否则保（对方新增 / 服务器新行经对方落盘）。
  for (const row of foreignById.values()) {
    if (ev.pendingDeleteIds.has(row.id) || ev.remoteTombstoneIds.has(row.id)) continue;
    out.push(row);
    changed = true;
  }

  if (!changed && out.length === local.length) return local; // 整表没变 → 原数组引用
  return out;
}

/** 五个实体表的整片合并；整片没变时返回 local 原引用（调用方可据此跳过 setState）。 */
export function mergeSnapshots(
  local: PersistedEntitiesSlice,
  foreign: PersistedEntitiesSlice,
  ev: CrossWindowEvidence,
): PersistedEntitiesSlice {
  const projects = mergeEntityLists(local.projects, foreign.projects, ev);
  const sections = mergeEntityLists(local.sections, foreign.sections, ev);
  const tasks = mergeEntityLists(local.tasks, foreign.tasks, ev);
  const tags = mergeEntityLists(local.tags, foreign.tags, ev);
  const comments = mergeEntityLists(local.comments, foreign.comments, ev);
  if (
    projects === local.projects &&
    sections === local.sections &&
    tasks === local.tasks &&
    tags === local.tags &&
    comments === local.comments
  ) {
    return local;
  }
  return { projects, sections, tasks, tags, comments };
}
